import { useCallback, useEffect, useMemo, useState } from 'react';

import useOrderHistories from './useOrderHistories';
import { loadOpportunities } from '../api';
import { handleApiErrorResponse } from '../handleErrors';
import { OpportunitiesResponse, OpportunityResponse, SystemHistoryEntry } from '~/types';

interface UseSystemHistoryParams {
  associationId?: string;
  orderId?: string;
  startDate?: string;
  endDate?: string;
}

const ACTIVE_STATUSES = [
  'PAYMENT_PENDING',
  'PAYMENT_APPROVED',
  'PAYMENT_REFUSED',
  'IN_EXECUTION',
  'ON_GOING',
  'MANUAL_ALLOCATION',
  'ALLOCATING',
];

const TERMINAL_STATUSES = ['FINISHED', 'CANCELED'];

const DEFAULT_LOOKBACK_DAYS = 30;
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 800;

function parseDateToMillis(date?: string | number) {
  if (typeof date === 'number') {
    return date;
  }

  if (!date) {
    return 0;
  }

  const parsed = new Date(date).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toIsoDateOnly(input: string | undefined, fallback: Date) {
  if (input) {
    return input;
  }
  return fallback.toISOString().slice(0, 10);
}

function buildDateRange(startDate?: string, endDate?: string) {
  const now = new Date();
  const lookbackStart = new Date();
  lookbackStart.setDate(now.getDate() - DEFAULT_LOOKBACK_DAYS);

  return {
    from: toIsoDateOnly(startDate, lookbackStart),
    to: toIsoDateOnly(endDate, now),
  };
}

function isRetryableError(error: any) {
  const status = error?.response?.status;
  if (typeof status === 'number') {
    return status >= 500 && status < 600;
  }
  return Boolean(error?.code === 'ECONNABORTED' || error?.message?.includes('Network'));
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function loadOpportunitiesWithRetry(statuses: string[], from: string, to: string) {
  let lastError: any;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await loadOpportunities(statuses, from, to);
    } catch (error) {
      lastError = error;
      if (!isRetryableError(error) || attempt === MAX_RETRIES) {
        throw error;
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError;
}

function inferEntryTypeFromStatus(status?: string): SystemHistoryEntry['entry_type'] {
  const loweredStatus = String(status || '').toLowerCase();

  if (
    loweredStatus.includes('cancel') ||
    loweredStatus.includes('refused') ||
    loweredStatus.includes('error') ||
    loweredStatus.includes('failed')
  ) {
    return 'EXIT';
  }

  return 'ENTRY';
}

function describeOpportunity(opportunity: OpportunityResponse) {
  const friendlyId = opportunity.number ? `#${opportunity.number}` : opportunity.id;
  const service = opportunity.service_description || 'Servico';
  return `${service} ${friendlyId} - ${opportunity.status}`;
}

export function useSystemHistory({ associationId, orderId, startDate, endDate }: UseSystemHistoryParams) {
  const { histories, loading: loadingOrderHistory } = useOrderHistories(orderId);

  const [opportunities, setOpportunities] = useState<OpportunitiesResponse>([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [opportunitiesError, setOpportunitiesError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => {
    setReloadKey(previous => previous + 1);
  }, []);

  useEffect(() => {
    if (!associationId) {
      setOpportunities([]);
      setOpportunitiesError(undefined);
      return undefined;
    }

    let cancelled = false;
    setLoadingOpportunities(true);
    setOpportunitiesError(undefined);

    const { from, to } = buildDateRange(startDate, endDate);

    Promise.allSettled([
      loadOpportunitiesWithRetry(ACTIVE_STATUSES, from, to),
      loadOpportunitiesWithRetry(TERMINAL_STATUSES, from, to),
    ])
      .then(results => {
        if (cancelled) {
          return;
        }

        const fulfilled: any[] = results.filter(result => result.status === 'fulfilled');
        const rejected: any[] = results.filter(result => result.status === 'rejected');

        const merged = fulfilled.flatMap(result => (result.value?.data || []) as OpportunitiesResponse);
        const filtered = merged.filter(item => item.association_id === associationId);
        setOpportunities(filtered);

        if (rejected.length > 0) {
          const parsed = handleApiErrorResponse(rejected[0].reason, 'Erro ao carregar histórico de pedidos');
          const message =
            parsed.handledMessage ||
            parsed.apiError ||
            parsed.applicationErrorMessage ||
            'Erro ao carregar histórico de pedidos';
          const partial = fulfilled.length > 0 ? ' (resultados parciais exibidos)' : '';
          setOpportunitiesError(`${message}${partial}`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingOpportunities(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [associationId, startDate, endDate, reloadKey]);

  const opportunityEntries = useMemo<SystemHistoryEntry[]>(() => {
    return opportunities.map(opportunity => ({
      id: `opportunity-${opportunity.id}`,
      created_at: opportunity.updated_at || opportunity.created_at,
      entry_type: inferEntryTypeFromStatus(opportunity.status),
      description: describeOpportunity(opportunity),
      amount: typeof opportunity.total === 'number' ? opportunity.total : undefined,
      source: 'ASSOCIATION_BALANCE',
      order_id: opportunity.id,
      contractor_id: opportunity.contractor_id,
      contractor_name: opportunity.contractor_name,
      customer_id: opportunity.customer_id,
      customer_name: opportunity.customer_name,
      association_id: opportunity.association_id,
      association_name: opportunity.association_name,
    }));
  }, [opportunities]);

  const orderHistoryEntries = useMemo<SystemHistoryEntry[]>(() => {
    return (histories || []).map(historyItem => ({
      id: `order-${historyItem.id}`,
      created_at: parseDateToMillis(historyItem.updated_at),
      entry_type: inferEntryTypeFromStatus(historyItem.title),
      description: `${historyItem.type}: ${historyItem.title}`,
      source: 'ORDER_HISTORY',
      order_id: orderId,
    }));
  }, [histories, orderId]);

  const data = useMemo<SystemHistoryEntry[]>(() => {
    return [...opportunityEntries, ...orderHistoryEntries].sort((a, b) => b.created_at - a.created_at);
  }, [opportunityEntries, orderHistoryEntries]);

  return {
    data,
    loading: (Boolean(associationId) && loadingOpportunities) || (Boolean(orderId) && loadingOrderHistory),
    error: opportunitiesError,
    orderHistory: histories,
    refetch,
  };
}

export default useSystemHistory;
